<template>
  <article :class="{ 'container-fluid': !isMobile, 'mx-n3': isMobile }">
    <div class="row">
      <div class="col-12">
        <SectionTitle class="my-2 pt-4" :title="title" :icon="icon" reverse />

        <DynamicFormList :items="items">
          <template #items="{ item, index }">
            <div class="col-12">
              <DynamicFormListItem
                :item="item"
                :no-actions="submitted"
                class="bg-light-gray border-radius--light"
              >
                <template #default="{ active, toggle }">
                  <SchoolInformationItem
                    :item="item"
                    :submitted="submitted"
                    cols="col-md col-12"
                  >
                    <template v-if="!submitted && !active" #actions>
                      <div
                        class="d-flex align-items-center justify-content-end h-100 w-100"
                      >
                        <MenuDropDown
                          title="Acciones"
                          :class="{ 'btn-block': isMobile }"
                          :items="[
                            {
                              label: 'Editar',
                              icon: 'fas fa-edit fa-fw text-green',
                              border: 'bottom',
                              click: toggle,
                            },
                            {
                              label: 'Eliminar',
                              icon: 'fas fa-times-circle fa-fw text-red',
                              click: () => remove(index),
                            },
                          ]"
                        />
                      </div>
                    </template>
                  </SchoolInformationItem>
                  <template v-if="!submitted && active">
                    <div class="delimiter" />
                    <SchoolInformationForm
                      :item="item"
                      :editting="active"
                      :collections="collections"
                      @submit="edit"
                      @cancel="toggle"
                    />
                  </template>
                </template>
              </DynamicFormListItem>
            </div>
          </template>

          <template v-if="!submitted" #form="{ toggle }">
            <SchoolInformationForm
              :collections="collections"
              @submit="add"
              @cancel="toggle"
            />
          </template>

          <template v-if="!submitted" #form-toggler="{ toggle }">
            <template v-if="showAddButtom">
              <div :class="['text-right', { 'mx-3': isMobile }]">
                <ActionButton
                  :class="{ 'btn-block': isMobile }"
                  text="Añadir"
                  variant="success"
                  i="fas fa-file-plus"
                  @click="toggle"
                />
              </div>
            </template>
            <template v-if="!hasAnyItems">
              <ActionButton
                type="activationButton"
                :text="principalButtonLabel"
                :class="{ invalid: validate }"
                @click="toggle"
              />
            </template>
            <CustomError
              v-if="validate"
              variant="no-border"
              :message="customErrorMsg"
            />
          </template>
        </DynamicFormList>

        <div v-if="showSubmittedWarningText && submitted && !hasAnyItems">
          <div class="bg-light-gray border-radius--light py-4 text-center">
            Al momento no cuenta con el personal en la escuela que pueda cubrir
            la necesidad de esta solicitud.
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import MenuDropDown from 'dropdown';
import CustomError from 'custom-error';
import { DynamicFormList, DynamicFormListItem } from '@/components/common';
import alert from '@/mixin/alert.mixin';
import SchoolInformationForm from './components/SchoolInformationForm';
import SchoolInformationItem from './components/SchoolInformationItem';

export default {
  name: 'SchoolInformation',
  mixins: [alert],
  components: {
    SectionTitle,
    ActionButton,
    MenuDropDown,
    CustomError,
    DynamicFormList,
    DynamicFormListItem,
    SchoolInformationForm,
    SchoolInformationItem,
  },
  props: {
    title: {
      type: String,
      default: 'Información de escuela',
    },
    icon: {
      type: String,
      default: 'fas fa-school',
    },
    items: {
      type: Array,
      default: () => [],
    },
    showSubmittedWarningText: {
      type: Boolean,
      default: false,
    },
    collections: {
      type: Object,
      required: true,
    },
    customErrorMsg: {
      type: String,
      default: 'Favor añadir al menos una escuela.',
      meta: {
        description: 'Mensaje de error encontrado',
        example: 'Firma requerida',
      },
    },
    principalButtonLabel: {
      type: String,
      default: 'Presione aquí para añadir escuela',
    },
    itemLabel: { type: String, default: 'Escuela' },
    submitted: { type: Boolean, default: false },
    allowAddMore: { type: Boolean, default: true },
    validate: { type: Boolean, default: false },
    limitNewAsisstantsTo: { type: [Number, String], default: 0 },
  },
  computed: {
    hasAnyItems() {
      return this.items.length > 0;
    },
    newSignatures() {
      return this.items.filter((x) => !x.id);
    },
    hasAnyNewItems() {
      return this.newSignatures.length > 0;
    },
    showAddButtom() {
      if (
        +this.limitNewAsisstantsTo > 0 &&
        this.allowAddMore &&
        this.hasAnyNewItems
      ) {
        return this.newSignatures.length < +this.limitNewAsisstantsTo;
      }
      return this.allowAddMore && this.hasAnyItems;
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
  },
  methods: {
    success() {
      this.ShowToast(
        'Confirmación',
        'La información de la escuela ha sido guardada exitosamente.',
        'success'
      );
    },
    add(item, reset) {
      this.$emit('update:validate', false);
      if (this.submitted) {
        return;
      }
      this.items.push(item);
      this.orderItems();
      reset();
      this.success();
    },
    edit(item, reset) {
      if (this.submitted) {
        return;
      }
      const index = this.items.findIndex((x) => x.id === item.id);
      this.items.splice(index, 1, item);
      this.orderItems();
      reset();
      this.success();
    },
    async remove(index) {
      const response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar la información de ${this.itemLabel.toLowerCase()}? `
      );
      if (!response || this.submitted) {
        return;
      }
      this.items.splice(index, 1);
    },
    orderItems() {
      return this.items.sort(function(a, b) {
        return new Date(a.fromDate) - new Date(b.fromDate);
      });
    },
  },
  watch: {
    items: {
      handler() {
        this.$emit('input', this.hasAnyItems);
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
::v-deep .invalid > button {
  background-color: #f7d7da;
  border: 1px solid #c5cdcf;
}
::v-deep .bg-error-outside > div > div {
  background: #ffebeb 0 0 no-repeat padding-box !important;
  border: 1px solid #c9545e !important;
}
::v-deep .bg-error-outside > div > div span.step-label {
  color: #c9545e;
}
::v-deep .bg-error-outside > div > div span.rounded-circle {
  border: 2px solid #c9545e !important;
}
.delimiter {
  border-top: 1px solid #e2e2e2;
  margin: 0.5rem 0;
}
</style>
